import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import classes from './AsyncList.module.css';
import useDataSource from 'Common/Utils/Hooks/useDataSource';
import { makeKeyGetter, makeLabelGetter } from 'Common/Utils/dataSourceHelpers';

function AsyncList({
    dataSource,
    error = false,
    keyField,
    helperText,
    label,
    labelField,
    selected,
    onChange,
    onLoad,
    onLoadError
}) {
    const [{ data, isLoading }] = useDataSource(dataSource, onLoadError, onLoad);
    const keyGetter = useMemo(() => makeKeyGetter(keyField), [keyField]);
    const labelGetter = useMemo(() => makeLabelGetter(labelField), [labelField]);

    const handleChange = useCallback((e, item) => {
        const checked = e.target && e.target.checked;
        const newSelected = data.filter(i => (checked && i === item) || (i !== item && selected?.includes(i)));
        onChange && onChange(newSelected);
    }, [selected, data, onChange]);

    const handleClearAll = useCallback(() => {
        onChange && onChange([]);
    }, [onChange]);

    const handleSelectAll = useCallback(() => {
        onChange && onChange([...data]);
    }, [onChange, data]);

    const listItems = useMemo(() => {
        if (isLoading) {
            return [
                <FormControlLabel
                    className={classes['async-list__progress']}
                    control={<CircularProgress color="inherit" size={14} />}
                    label={'Loading...'}
                    key={-1}
                />
            ];
        }

        return (data || []).map((item, index) => {
            return (
                <FormControlLabel
                    control={<Checkbox checked={selected?.includes(item)} onChange={e => handleChange(e, item)} />}
                    label={labelGetter(item, index)}
                    key={keyGetter(item, index)}
                />
            );
        });
    }, [data, isLoading, selected, labelGetter, keyGetter, handleChange]);

    return (
        <FormControl className={classes['async-list__container']} error={error}>
            <div className="MuiInput-underline">
                <InputLabel shrink required>{label}</InputLabel>
                <FormGroup>
                    {listItems}
                </FormGroup>
                {helperText && (
                    <FormHelperText>{helperText}</FormHelperText>
                )}
                {!isLoading && (
                    <div className={classes['async-list__commands']}>
                        <Button size="small" onClick={handleClearAll}>
                            Clear All
                        </Button>
                        <Button size="small" onClick={handleSelectAll}>
                            Select All
                        </Button>
                    </div>
                )}
            </div>
        </FormControl>
    );
}

AsyncList.propTypes = {
    dataSource: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.func,
        PropTypes.instanceOf(Promise)
    ]),
    keyField: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
    label: PropTypes.string.isRequired,
    labelField: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
    ]),
    selected: PropTypes.array,
    onChange: PropTypes.func,
    onLoad: PropTypes.func,
    onLoadError: PropTypes.func
}

export default React.memo(AsyncList);
